<script setup lang="ts">
import { contentPageTitle } from '@@/constants/pageMeta'

definePageMeta({
  layout: 'website',
})

const route = useRoute()

const { data: entry } = await useAsyncData<any>(`changelog-${route.path}`, async () => {
  return await queryCollection('changelog').where('path', '=', route.path).first()
})

if (!entry.value) {
  throw createError({ statusCode: 404, statusMessage: 'Release notes not found' })
}

const { data: siblings } = await useAsyncData<any[]>(
  `changelog-siblings-${route.path}`,
  async () => {
    const all = await queryCollection('changelog').all()
    return all.sort((a: any, b: any) => {
      const da = new Date(changelogDate(a.meta)).getTime()
      const db = new Date(changelogDate(b.meta)).getTime()
      return db - da
    })
  },
  { watch: [() => route.path] }
)

const currentIndex = computed(() => {
  if (!siblings.value) return -1
  return siblings.value.findIndex((s: any) => s.path === route.path)
})

const newerEntry = computed(() => {
  const i = currentIndex.value
  if (i <= 0 || !siblings.value) return null
  return siblings.value[i - 1]
})

const olderEntry = computed(() => {
  const i = currentIndex.value
  if (i < 0 || !siblings.value) return null
  if (i >= siblings.value.length - 1) return null
  return siblings.value[i + 1]
})

// Frontmatter `seo` sets the search description; without one, the release's
// opening paragraph. Checked by tests/seo/pageMeta.test.ts.
usePageMeta(() => ({
  title: contentPageTitle(entry.value.seo?.title || entry.value.title),
  description: entry.value.seo?.description || articleFirstParagraph(entry.value),
}))

useHead({
  script: [
    jsonLdScript(
      'breadcrumb-jsonld',
      breadcrumbJsonLd([
        { name: 'Changelog', path: '/changelog' },
        { name: entry.value.title, path: route.path },
      ])
    ),
  ],
  meta: [
    { property: 'og:type', content: 'article' },
    ...(entry.value?.meta?.date ? [{ property: 'article:published_time', content: entry.value.meta.date }] : []),
    ...(entry.value?.meta?.updated ? [{ property: 'article:modified_time', content: entry.value.meta.updated }] : []),
  ],
})
</script>

<template>
  <div v-if="entry" class="changelog-detail-page resource-reading-page">
    <v-container class="resources-container changelog-reading-container">
      <NuxtLink to="/changelog" class="resources-back">
        <v-icon icon="$arrowLeft" size="18" />
        All updates
      </NuxtLink>

      <header class="resource-reading-header changelog-reading-header">
        <ChangelogReleaseMeta :meta="entry.meta" :latest="currentIndex === 0" />
        <h1>{{ entry.title }}</h1>
      </header>

      <div class="changelog-content-body">
        <ContentRenderer :value="entry" />
      </div>

      <PageFeedback :title="entry.title" context="Changelog" question="Was this update useful?" />

      <nav v-if="olderEntry || newerEntry" class="changelog-nav" aria-label="More releases">
        <v-card v-if="olderEntry" :to="olderEntry.path" class="nav-card">
          <div class="nav-direction">
            <v-icon icon="$arrowLeft" size="18" />
            Older release
          </div>
          <div class="nav-title">{{ olderEntry.title }}</div>
        </v-card>
        <v-card v-if="newerEntry" :to="newerEntry.path" class="nav-card nav-card-newer">
          <div class="nav-direction">
            Newer release
            <v-icon icon="$arrowRight" size="18" />
          </div>
          <div class="nav-title">{{ newerEntry.title }}</div>
        </v-card>
      </nav>
    </v-container>
  </div>

  <div v-else class="d-flex justify-center align-center" style="min-height: 500px">
    <v-progress-circular indeterminate color="primary" />
  </div>
</template>

<style scoped>
.changelog-reading-container.v-container {
  max-width: 800px;
  padding-top: 32px;
  padding-bottom: 64px;
}

.changelog-reading-header {
  margin-bottom: 32px;
}

.changelog-reading-header h1 {
  margin-top: 20px;
}

.changelog-nav {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 40px;
}

.nav-card {
  padding: 20px;
  min-width: 0;
}

.nav-card-newer {
  grid-column: 2;
  text-align: right;
}

.nav-direction {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(var(--v-theme-on-surface), 0.7);
  font-size: 0.8125rem;
  margin-bottom: 8px;
}

.nav-card-newer .nav-direction {
  justify-content: flex-end;
}

.nav-title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

@media (max-width: 599px) {
  .changelog-nav {
    grid-template-columns: minmax(0, 1fr);
  }

  .nav-card-newer {
    grid-column: 1;
  }
}
</style>

<style>
.changelog-content-body {
  font-size: 1.0625rem;
  line-height: 1.8;
  overflow-wrap: anywhere;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.changelog-content-body h2 {
  font-size: 1.375rem;
  font-weight: 700;
  margin-bottom: 1rem;
  margin-top: 2rem;
  color: rgb(var(--v-theme-on-surface));
}

.changelog-content-body h2:first-child {
  margin-top: 0;
}

.changelog-content-body h3 {
  font-size: 1.25rem;
  line-height: 1.4;
  font-weight: 600;
  margin-top: 2.5rem;
  margin-bottom: 0.875rem;
  color: rgb(var(--v-theme-on-surface));
}

.changelog-content-body h3 > a {
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}

.changelog-content-body ul,
.changelog-content-body ol {
  padding-left: 1.25rem;
  margin-bottom: 1.5rem;
}

.changelog-content-body li {
  margin-bottom: 0.875rem;
  padding-left: 0.375rem;
  line-height: 1.7;
}

.changelog-content-body strong,
.changelog-content-body b {
  color: rgb(var(--v-theme-on-surface));
  font-weight: 600;
}

.changelog-content-body p {
  margin-bottom: 1rem;
  line-height: 1.8;
}

.changelog-content-body img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 1.25rem 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.changelog-content-body code {
  background: rgba(var(--v-theme-primary), 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
}

.changelog-content-body a {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  text-underline-offset: 3px;
  font-weight: 500;
}

.changelog-content-body :is(h2, h3) {
  scroll-margin-top: 96px;
}

.changelog-content-body :is(h2, h3) > a {
  color: inherit;
  font-weight: inherit;
  text-decoration: none;
}

.changelog-content-body :is(h2, h3) > a:hover {
  text-decoration: underline;
}
</style>
